import { Injectable } from '@angular/core';

import { finalize, map, Observable, of } from 'rxjs';

import { Division } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';
import { observableInSeparateThread } from '../helper/observable-helper';

/**
 * Service to get and save Division data. This division data is specific to
 * wrestling and is used to group/categorize the wrestlers
 */
@Injectable()
export class DivisionService {
    public static INCLUDE_WEIGHT_CLASSES: string = 'weightClasses';
    public static INCLUDE_DIV_REGISTRATION: string = 'divisionRegistration.teams.coaches';
    public static INCLUDE_SKILL_LEVELS: string = 'skillLevels';
    public static INCLUDE_SEEDING_CRITERIA: string = 'seedingCriteria';
    public static INCLUDE_USAW_MEMBERSHIPS: string = 'usawMemberships';

    private cachedDivisionsMap: { [apiRequest: string]: Division[]; } = {};

    constructor(private http: HttpServiceHelper) {
    }

    public clearCache(): void {
        this.cachedDivisionsMap = {};
    }

    /**
     * Get all divisions for the specified event
     */
    public getIndex(eventId: string, includes: string[] = null, forceRefresh: boolean = false): Observable<Division[]> {
        const queryParams: string[] = this.appendGetFields();
        queryParams.push('page[size]=500');
        const apiRequestUrl: string = this.getApiUrl(eventId, null, includes, queryParams);

        if (!forceRefresh && this.cachedDivisionsMap[apiRequestUrl]) {
            // if cache is available just return it as `Observable`
            return observableInSeparateThread(
                this.cachedDivisionsMap[apiRequestUrl].map((d: Division) => d.clone()),
            );
        }
        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const divisions: Division[] = [];
                const bodyData: any = response.data || [];
                for (const division of bodyData) {
                    divisions.push((new Division(eventId)).deserialize(division, response.included));
                }

                this.cachedDivisionsMap[apiRequestUrl] = divisions.map((d: Division) => d.clone());

                return divisions;
            }));
    }

    /**
     * Get a single division's data
     */
    public get(eventId: string, divisionId: string, includes: string[]): Observable<Division> {
        const queryParams: string[] = this.appendGetFields();
        const apiRequestUrl: string = this.getApiUrl(eventId, divisionId, includes, queryParams);

        return this.http.get(apiRequestUrl)
            .pipe(
                map((response: IJsonApiResponse) => (new Division(eventId).deserialize(response, response.included))),
            );
    }

    /**
     * Add or update the division
     */
    public save(division: Division): Observable<Division> {
        let response: Observable<IJsonApiResponse>;
        const queryParams: string[] = this.appendGetFields();
        const apiRequestUrl: string =
            this.getApiUrl(division.eventId, division.id, [DivisionService.INCLUDE_WEIGHT_CLASSES], queryParams);

        if (division.id) {
            // Update existing Division
            response = this.http.patch(apiRequestUrl, division.serialize());
        } else {
            // Add a new Division
            response = this.http.post(apiRequestUrl, division.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new Division(division.eventId))
                    .deserialize(response, response.included)),
                finalize(() => this.clearCache()),
            );
    }

    /**
     * Delete the specified division.
     */
    public delete(eventId: string, divisionId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, divisionId, null);

        return this.http.delete(apiRequestUrl)
            .pipe(
                map((response: IJsonApiResponse) => true),
                finalize(() => this.clearCache()),
            );
    }

    private appendGetFields(queryParams: string[] = []): string[] {
        queryParams = queryParams || [];

        // Only fetch specific fields
        if (queryParams.filter((x: string) => x.indexOf('fields[division]') !== -1).length === 0) {
            queryParams.push(`fields[division]=${Division.getFields().join(',')}`);
        }

        return queryParams;
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(eventId: string, divisionId: string, includes: string[], queryParams: string[] = null): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/divisions/`;

        if (divisionId) {
            url += divisionId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}
